import { Sequence } from '@repo/sdk/models/components';

/**
 * Finds columns where all values match any of the target values.
 * Skips the 0th column as it's always empty by design.
 *
 * @param sequence - The sequence to check
 * @param targetValues - Array of values to check for (e.g., [0, 1] to find columns with all 0s or all 1s)
 * @returns Array of column indices that match
 */
export const findColumnWithValue = (sequence: Sequence, targetValues: number[]): number[] => {
  if (!sequence.table || sequence.table.length === 0) {
    return [];
  }

  const matchingColumns: number[] = [];
  const maxStages = Math.max(...sequence.table.map((item) => item.stageValues.length));

  // Start at 1 to skip the 0th column (always empty by design)
  for (let colIndex = 1; colIndex < maxStages; colIndex++) {
    const hasMatchingValue = sequence.table.every((item) => {
      const value = item.stageValues[colIndex];
      return targetValues.includes(value);
    });

    if (hasMatchingValue) {
      matchingColumns.push(colIndex);
    }
  }

  return matchingColumns;
};

/**
 * Finds columns that are duplicates of other columns.
 *
 * @param sequence - The sequence to check
 * @returns Array of column indices that are duplicates
 */
export const findDuplicateColumns = (sequence: Sequence): number[] => {
  if (!sequence.table || sequence.table.length === 0) {
    return [];
  }

  const duplicateColumns: number[] = [];
  const maxStages = Math.max(...sequence.table.map((item) => item.stageValues.length));

  // Start at 1 to skip the 0th column
  for (let i = 1; i < maxStages - 1; i++) {
    const column = sequence.table.map((item) => item.stageValues[i]);

    for (let j = i + 1; j < maxStages; j++) {
      const otherColumn = sequence.table.map((item) => item.stageValues[j]);

      if (column.every((value, index) => value === otherColumn[index])) {
        if (!duplicateColumns.includes(i)) {
          duplicateColumns.push(i);
        }
        if (!duplicateColumns.includes(j)) {
          duplicateColumns.push(j);
        }
      }
    }
  }

  return duplicateColumns.sort();
};

/**
 * Groups columns that have identical values.
 *
 * @param sequence - The sequence to check
 * @returns Array of arrays, where each inner array contains column indices that are identical
 */
export const groupIdenticalColumns = (sequence: Sequence): number[][] => {
  if (!sequence.table || sequence.table.length === 0) {
    return [];
  }

  const maxStages = Math.max(...sequence.table.map((item) => item.stageValues.length));

  // Need at least 3 columns (0th hidden, and 2 actual columns) to have duplicates
  if (maxStages < 3) {
    return [];
  }

  const columnStrings: { sequence: string; index: number }[] = [];

  // Start at 1 to skip the 0th column
  for (let colIndex = 1; colIndex < maxStages; colIndex++) {
    const columnValues = sequence.table.map((item) => item.stageValues[colIndex]);
    columnStrings.push({
      sequence: columnValues.join(','),
      index: colIndex,
    });
  }

  const groups: Record<string, number[]> = columnStrings.reduce(
    (acc: Record<string, number[]>, item: { sequence: string; index: number }) => {
      const { sequence: seq, index } = item;
      acc[seq] = acc[seq] ?? [];
      acc[seq].push(index);
      return acc;
    },
    {}
  );

  return Object.values(groups)
    .filter((group) => group.length > 1)
    .map((group) => group.sort());
};
